import type {
  NotificationEventType,
  NotificationDirection,
} from '../../types/notifications.types';

/**
 * Per-event-type visual config for the notifications page.
 *
 * Each entry pairs:
 *   - `emoji`: a leading glyph that mirrors the Slack rendering
 *     convention (🤖 = automation, ✋ = manual order, etc.). Keep
 *     this in sync with `services/position-unwinder/src/notification/
 *     slack-message.builder.ts` so the in-app feed and Slack channel
 *     are visually parallel.
 *   - `color`: hex used for the row's left-border accent + the type
 *     chip's outline. Themed by category so the operator can scan
 *     the feed by color (greens = success, reds = errors, blues =
 *     placement, purples = manual, oranges/yellows = warnings).
 *   - `label` (optional): friendlier display text for the chip when
 *     the raw event-type id is too terse. Defaults to the type id.
 *
 * Keeping the config as a single typed map rather than spreading it
 * across components means a new event type only needs one entry to
 * become first-class on the page (chip + border + emoji + summary
 * row). The `Record<NotificationEventType, _>` shape is enforced
 * exhaustively at compile time.
 */
export interface NotificationDisplayConfig {
  emoji: string;
  color: string;
  label?: string;
}

export const NOTIFICATION_DISPLAY: Record<
  NotificationEventType,
  NotificationDisplayConfig
> = {
  // ─── Order lifecycle (automation, UI-only) ─────────────────────────────
  // Blues for placement / amend so the resting-order family reads as
  // "automation moving the book" at a glance.
  maker_placed: { emoji: '🤖', color: '#2196f3', label: 'maker placed' },
  maker_adjusted: { emoji: '🔧', color: '#03a9f4', label: 'amended' },
  maker_cancelled: { emoji: '🚫', color: '#607d8b', label: 'cancelled' },
  // Yellow/amber on partial fills to flag "in motion, not done".
  maker_partial_fill: {
    emoji: '🟡',
    color: '#fdd835',
    label: 'partial fill',
  },
  partial_fill_accumulated: {
    emoji: '📥',
    color: '#ffc107',
    label: 'accumulated',
  },
  // Green for the happy path (full fill, counter, cycle done).
  maker_filled: { emoji: '✅', color: '#4caf50', label: 'filled' },
  counter_placed: { emoji: '🤖', color: '#00bcd4', label: 'counter placed' },
  // Light teal — counter leg fill confirmation, sequenced between
  // `counter_placed` (deep cyan) and `execution_complete` (deep
  // green). Matches the Slack card color (#26a69a).
  counter_filled: { emoji: '✅', color: '#26a69a', label: 'counter filled' },
  crossing_executed: {
    emoji: '⚡',
    color: '#8bc34a',
    label: 'crossing executed',
  },
  crossing_partial: {
    emoji: '⚡',
    color: '#cddc39',
    label: 'crossing partial',
  },
  crossing_accumulated: {
    emoji: '⚡',
    color: '#ffeb3b',
    label: 'crossing accumulated',
  },
  // ─── Manual orders (UI-only with source filter) ────────────────────────
  // Purples so manual events pop visually against the automation feed.
  manual_order_placed: {
    emoji: '✋',
    color: '#9c27b0',
    label: 'manual placed',
  },
  manual_order_executed: {
    emoji: '✋',
    color: '#673ab7',
    label: 'manual filled',
  },
  // ─── UI + Slack (money / errors / TNA / digest) ────────────────────────
  execution_complete: {
    emoji: '🎯',
    color: '#2e7d32',
    label: 'cycle done',
  },
  // Reds for everything that needs operator eyes — error severity
  // shades from orange (warnings) → red (errors) → very dark red
  // (terminal / emergency).
  error: { emoji: '🚨', color: '#f44336' },
  counter_failed: { emoji: '🚨', color: '#d32f2f', label: 'counter failed' },
  out_of_balance: {
    emoji: '💸',
    color: '#ff5722',
    label: 'out of balance',
  },
  rule_below_minimum: {
    emoji: '⚠️',
    color: '#ff9800',
    label: 'below min',
  },
  max_volume_reached: {
    emoji: '🛑',
    color: '#e67e22',
    label: 'max volume',
  },
  dust_remaining: { emoji: '🌑', color: '#795548', label: 'dust' },
  market_takeover: {
    emoji: '🌪️',
    color: '#e91e63',
    label: 'market takeover',
  },
  tna_negative: { emoji: '🔻', color: '#c62828', label: 'TNA negative' },
  net_contracts_digest: {
    emoji: '📊',
    color: '#3f51b5',
    label: 'digest',
  },
  emergency_stop: {
    emoji: '🛑',
    color: '#b71c1c',
    label: 'emergency stop',
  },
  emergency_resume: {
    emoji: '▶️',
    color: '#388e3c',
    label: 'emergency resume',
  },
  // Teal for the recovery family — adjacent to green (success) but
  // distinct, so the operator sees "post-incident reconciliation"
  // without confusing it with normal fills.
  recovery_filled: {
    emoji: '🔄',
    color: '#009688',
    label: 'recovery filled',
  },
  recovery_restored: {
    emoji: '🔄',
    color: '#26a69a',
    label: 'recovery restored',
  },
  counter_skipped: {
    emoji: '⏭️',
    color: '#757575',
    label: 'counter skipped',
  },
  // ─── Slack only (ops monitoring) ───────────────────────────────────────
  // Greys — these don't surface on the page today (routing config
  // sends them to Slack only) but the entries exist so the type
  // map is exhaustive and a future routing flip needs zero UI work.
  platform_disconnect: {
    emoji: '🔌',
    color: '#424242',
    label: 'platform disconnect',
  },
  platform_reconnect: {
    emoji: '🔋',
    color: '#66bb6a',
    label: 'platform reconnect',
  },
  rule_started: { emoji: '▶️', color: '#26a69a', label: 'rule started' },
  rule_completed: { emoji: '🏁', color: '#7cb342', label: 'rule completed' },
  // ─── Position opener (BUY-direction) ─────────────────────────────────
  // Paired events render IDENTICALLY to their unwinder analogues — same
  // emoji, same color, same label. The "entry" vs "unwind" axis is
  // surfaced as a separate `direction` chip on the row (see
  // `getNotificationDirection` below) so the operator filters by
  // direction without losing pattern recognition on the kind of event
  // (a fill is a fill, regardless of which side opened or closed it).
  // Only `entry_constraint_violated` deviates with a unique emoji + color
  // because it has no unwinder analogue — it's the hard $1 cap breach
  // and must be visually unmistakable.
  entry_started: { emoji: '▶️', color: '#26a69a', label: 'rule started' },
  entry_completed: { emoji: '🏁', color: '#7cb342', label: 'rule completed' },
  entry_maker_placed: { emoji: '🤖', color: '#2196f3', label: 'maker placed' },
  entry_maker_adjusted: { emoji: '🔧', color: '#03a9f4', label: 'amended' },
  entry_maker_cancelled: {
    emoji: '🚫',
    color: '#607d8b',
    label: 'cancelled',
  },
  entry_maker_partial_fill: {
    emoji: '🟡',
    color: '#fdd835',
    label: 'partial fill',
  },
  entry_partial_fill_accumulated: {
    emoji: '📥',
    color: '#ffc107',
    label: 'accumulated',
  },
  entry_maker_filled: { emoji: '✅', color: '#4caf50', label: 'filled' },
  entry_counter_placed: {
    emoji: '🤖',
    color: '#00bcd4',
    label: 'counter placed',
  },
  entry_counter_filled: {
    emoji: '✅',
    color: '#26a69a',
    label: 'counter filled',
  },
  entry_crossing_executed: {
    emoji: '⚡',
    color: '#8bc34a',
    label: 'crossing executed',
  },
  entry_crossing_partial: {
    emoji: '⚡',
    color: '#cddc39',
    label: 'crossing partial',
  },
  entry_crossing_accumulated: {
    emoji: '⚡',
    color: '#ffeb3b',
    label: 'crossing accumulated',
  },
  entry_complete: { emoji: '🎯', color: '#2e7d32', label: 'cycle done' },
  // The hard $1 cap trip — opener-only (no unwinder analogue) so this
  // entry uses a distinct emoji and color. 🛡️ = the cap fortress was
  // breached; deep red sets it apart from generic errors. Single most
  // important operator-actionable signal in the opener service.
  entry_constraint_violated: {
    emoji: '🛡️',
    color: '#b71c1c',
    label: 'hard-cap violated',
  },
  entry_error: { emoji: '🚨', color: '#f44336' },
  entry_counter_failed: {
    emoji: '🚨',
    color: '#d32f2f',
    label: 'counter failed',
  },
  entry_below_minimum: { emoji: '⚠️', color: '#ff9800', label: 'below min' },
  entry_max_volume_reached: {
    emoji: '🛑',
    color: '#e67e22',
    label: 'max volume',
  },
  entry_dust_remaining: { emoji: '🌑', color: '#795548', label: 'dust' },
  entry_market_takeover: {
    emoji: '🌪️',
    color: '#e91e63',
    label: 'market takeover',
  },
  entry_emergency_stop: {
    emoji: '🛑',
    color: '#b71c1c',
    label: 'emergency stop',
  },
  entry_emergency_resume: {
    emoji: '▶️',
    color: '#388e3c',
    label: 'emergency resume',
  },
  entry_recovery_filled: {
    emoji: '🔄',
    color: '#009688',
    label: 'recovery filled',
  },
  entry_recovery_restored: {
    emoji: '🔄',
    color: '#26a69a',
    label: 'recovery restored',
  },
  entry_counter_skipped: {
    emoji: '⏭️',
    color: '#757575',
    label: 'counter skipped',
  },
  entry_manual_order_placed: {
    emoji: '✋',
    color: '#9c27b0',
    label: 'manual placed',
  },
  entry_manual_order_executed: {
    emoji: '✋',
    color: '#673ab7',
    label: 'manual filled',
  },
};

/**
 * Direction axis: which side of the arbitrage lifecycle a notification
 * is about. Surfaced as a chip on each row so the operator can scan
 * "open" vs "close" without disambiguating from the chip label (which
 * is intentionally identical for paired events — a `maker_placed` and
 * an `entry_maker_placed` are the same kind of event, just on opposite
 * sides of the cycle).
 *
 *   - `entry`  — opener service (BUY direction, OPENING a position)
 *   - `unwind` — unwinder service (SELL direction, CLOSING a position)
 *   - `system` — direction-less ops events (platform disconnect, digest,
 *                tna_negative, out_of_balance, manual orders, emergency,
 *                rule_started/completed without an entry/unwind context)
 *
 * Mapping is purely a function of `EventType`, not the payload. That
 * keeps producers from having to thread a direction field through and
 * means a backend rename is a one-line frontend change.
 */
const SYSTEM_TYPES: ReadonlySet<string> = new Set([
  'platform_disconnect',
  'platform_reconnect',
  'net_contracts_digest',
  'tna_negative',
  'out_of_balance',
  'emergency_stop',
  'emergency_resume',
  'manual_order_placed',
  'manual_order_executed',
]);

export function getNotificationDirection(
  type: NotificationEventType | string,
): NotificationDirection {
  if (typeof type === 'string' && type.startsWith('entry_')) return 'entry';
  if (SYSTEM_TYPES.has(type)) return 'system';
  return 'unwind';
}

/**
 * Display config for the direction chip itself. Kept beside the per-
 * type display so a single import in the page component covers both.
 */
export const DIRECTION_DISPLAY: Record<
  NotificationDirection,
  { color: string; label: string }
> = {
  // Soft purple — direction is its own dimension, distinct from
  // severity (red/orange/info) and source (chip outline). Picking a
  // hue not used elsewhere on the row keeps the visual hierarchy
  // unambiguous.
  entry: { color: '#7e57c2', label: 'Entry' },
  unwind: { color: '#5c6bc0', label: 'Unwind' },
  system: { color: '#78909c', label: 'System' },
};

const FALLBACK: NotificationDisplayConfig = {
  emoji: '📌',
  color: '#9e9e9e',
};

/**
 * Returns the display config for a given event type, falling back to
 * a neutral grey pin if the type is unknown — defensive against
 * backend rolling out a new event before the frontend is updated.
 */
export function getNotificationDisplay(
  type: NotificationEventType | string,
): NotificationDisplayConfig {
  return (
    (NOTIFICATION_DISPLAY as Record<string, NotificationDisplayConfig>)[type] ??
    FALLBACK
  );
}
